import { trans } from "@mongez/localization";
import { parseError, useSubmitProps } from "core/shared";
import { User, userAtom } from "core/user";
import { action } from "framework/config/action";
import { URLS } from "framework/config/urls";

export function useSetShippingAddress({ onSuccess, onError }: useSubmitProps) {
  const submit = action(URLS.checkout.root);

  const setShippingAddress = async (addressId: number) => {
    const response = await submit({
      action: "setShippingAddress",
      addressId,
    });

    if (response.isOK) {
      onSuccess?.(response.data);
    } else {
      onError?.(parseError(response));
    }
  };

  return { setShippingAddress };
}

export function useGuestSetShippingAddress({
  onSuccess,
  onError,
}: useSubmitProps) {
  const submit = action(URLS.checkout.guest);

  const setGuestShippingAddress = async ({ values, form }) => {
    const response = await submit({
      action: "setShippingAddress",
      ...values,
    });

    form.submitting(false);

    if (response.isOK) {
      onSuccess?.(response.data);
    } else {
      onError?.(parseError(response));
    }
  };

  return { setGuestShippingAddress };
}

export function useSetPaymentMethod({ onSuccess, onError }: useSubmitProps) {
  const user = userAtom.useValue();
  const submit = action(
    user.userType === "guest" ? URLS.checkout.guest : URLS.checkout.root,
  );

  const setPaymentMethod = async (methodName: string) => {
    const response = await submit({
      action: "setPaymentMethod",
      methodName,
    });

    if (response.isOK) {
      onSuccess?.(response.data);
    } else {
      onError?.(parseError(response));
    }
  };

  return { setPaymentMethod };
}

export function useSetShippingMethod({ onSuccess, onError }: useSubmitProps) {
  const user = userAtom.useValue();
  const submit = action(
    user.userType === "guest" ? URLS.checkout.guest : URLS.checkout.root,
  );

  const setShippingMethod = async (methodName: string) => {
    const response = await submit({
      action: "setShippingMethod",
      methodName,
    });

    if (response.isOK) {
      onSuccess?.(response.data);
    } else {
      onError?.(parseError(response));
    }
  };

  return { setShippingMethod };
}

export function useSetPromoCode({ onSuccess, onError }: useSubmitProps) {
  const user = userAtom.useValue();
  const submit = action(
    user.userType === "guest" ? URLS.checkout.guest : URLS.checkout.root,
  );

  const setPromoCode = async (code: string) => {
    const response = await submit({
      action: "setPromoCode",
      code,
    });

    if (response.isOK) {
      onSuccess?.(response.data);
    } else {
      onError?.(parseError(response));
    }
  };

  return { setPromoCode };
}

export function useRemovePromoCode({ onSuccess, onError }: useSubmitProps) {
  const user = userAtom.useValue();
  const submit = action(
    user.userType === "guest" ? URLS.checkout.guest : URLS.checkout.root,
  );

  const removePromoCode = async () => {
    const response = await submit({
      action: "removePromoCode",
    });

    if (response.isOK) {
      onSuccess?.(response.data);
    } else {
      onError?.(parseError(response));
    }
  };

  return { removePromoCode };
}

export function usePlaceOrderCheckout({ onSuccess, onError }: useSubmitProps) {
  const user: User = userAtom.useValue();
  const submit = action(
    user.userType === "guest" ? URLS.checkout.guest : URLS.checkout.root,
  );

  const placeOrder = async ({ values, form }) => {
    const {
      paymentMethod,
      code,
      shippingAddress,
      billingAddress,
      pickupDate,
      pickupTime,
      pickupDate_inner,
      eventDate_inner,
      eventTime,
      ...data
    } = values;

    if (!paymentMethod) {
      onError?.(trans("checkout.paymentMethodRequired"));
      return;
    }

    const response = await submit({
      action: "placeOrder",
      ...data,
    });

    userAtom.change("totalCart", 0);

    form.submitting(false);

    if (response.isOK) {
      onSuccess?.(response.data);
    } else {
      onError?.(parseError(response));
    }
  };

  return { placeOrder };
}

export function useVerifyPayment({ onSuccess, onError }: useSubmitProps) {
  const user = userAtom.useValue();

  const submit = action(
    user.userType === "guest" ? URLS.checkout.guest : URLS.checkout.root,
  );

  const verifyPayment = async (data: any) => {
    const response = await submit({
      action: "verifyPayment",
      ...data,
    });

    if (response.isOK) {
      onSuccess?.(response.data);
    } else {
      onError?.(parseError(response));
    }
  };

  return { verifyPayment };
}
